import { Edit } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { stripeUrl } from "common/const";
import {
  AnyPermissionsKey,
  PermissionsKeys,
} from "components/permissions/permission-keys";
import { EditPaymentMethodDialogue } from "components/settings/shared/billing-details/editPaymentDialogue";
import { User, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { HubPlan, PaymentMethod } from "data/models";
import { ReactQueryMutationError, addPaymentMethod } from "data/mutations";
import { fetchPaymentMethod } from "data/queries";
import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toTitleCase } from "utils/commons";
import { resolveIdentityId } from "utils/identity";
import { ChangeSummary } from "./organisation-manage-subscription";

type ConfirmChangesProps = {
  changes: ChangeSummary;
  canSubmit: boolean;
  isLoading: boolean;
  onSubmit: () => void;
  canCancel: boolean;
  onCancel: () => void;
  selectedPlan: HubPlan | null;
  trialSelected: boolean;
  newPlanIsPermitted: (keys: AnyPermissionsKey[]) => boolean;
};
export function ConfirmChangesSection(props: ConfirmChangesProps) {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const paymentMethodQuery = useQuery({
    queryKey: [QueryKeys.paymentMethod, identityId],
    queryFn: () => fetchPaymentMethod(),
  });

  const stripe = useStripe();
  const stripeElements = useElements();

  const [editingCC, setEditingCC] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const paymentMethodMutation = useMutation({
    mutationFn: () => addPaymentMethod(stripe, stripeElements, setEditingCC),
    onSuccess: (newPaymentMethod) => {
      queryClient.setQueryData(
        [QueryKeys.paymentMethod, identityId],
        newPaymentMethod,
      );
    },
  });

  const isExternallyManaged = false;

  const handleOnClose = () => {
    paymentMethodMutation.reset();
    setEditingCC(false);
  };

  const { changes, canSubmit, isLoading, onSubmit } = props;

  const [identity, setIdentity] = useIdentity();
  const user = identityState as User;

  return (
    <Card>
      <CardHeader title="Changes to Subscription" />
      <CardContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Alert variant="outlined" severity="info">
            {props.changes.infoText}
          </Alert>

          {changes.changeType != "no_selection" &&
            changes.changeType != "same" && (
              <>
                {/* Subscription Summary */}
                <Box display="flex" flexDirection="column" gap={1}>
                  {changes.changeType == "downgrade" &&
                    !props.newPlanIsPermitted([
                      PermissionsKeys.membersUpdate,
                    ]) && (
                      <Alert severity="warning" variant="outlined">
                        <strong>WARNING,</strong>
                        All members who are not the <strong>owner</strong> will
                        have their access to Hub disabled when your subscription
                        downgrades.
                      </Alert>
                    )}
                  <Typography variant="h6">Subscription</Typography>
                  {changes.subscription.map((item, index) => (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent="space-between"
                      paddingLeft={2}
                    >
                      <Typography variant="body1">{item.lhs}</Typography>
                      <Typography variant="body1">{item.rhs}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Charges */}
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="h6">Charges</Typography>
                  {changes.charges
                    .filter((x) => !x.hidden)
                    .map((charge, index) => (
                      <Box
                        key={index}
                        display="flex"
                        justifyContent="space-between"
                        paddingLeft={2}
                      >
                        <Typography variant="body1">{charge.lhs}</Typography>
                        <Typography variant="body1">{charge.rhs}</Typography>
                      </Box>
                    ))}
                </Box>

                {/* Total Due */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  paddingLeft={2}
                >
                  <Typography variant="h6">Total Due</Typography>
                  <Typography variant="h6" fontWeight={"bold"}>
                    {changes.totalDue}
                  </Typography>
                </Box>
                {changes.trialChargeWarning && (
                  <Alert severity="warning">{changes.trialChargeWarning}</Alert>
                )}
              </>
            )}
          {paymentMethodQuery.isSuccess &&
            !isExternallyManaged &&
            !props.selectedPlan?.free_plan && (
              <Fragment>
                <Typography variant="h5">Payment Method</Typography>
                <TextField
                  size="small"
                  error={paymentMethodQuery.data === null}
                  id="paymentMethod"
                  name="paymentMethod"
                  label="Payment Method"
                  type="text"
                  value={
                    paymentMethodQuery.data
                      ? `${toTitleCase(paymentMethodQuery.data.method)} .... ${
                          paymentMethodQuery.data.last4
                        } - ${paymentMethodQuery.data.expireMonth}/${
                          paymentMethodQuery.data.expireYear
                        }`
                      : "No Payment Method"
                  }
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={() => setEditingCC(true)}>
                        <Edit />
                      </IconButton>
                    ),
                  }}
                />
                {paymentMethodQuery.data && paymentMethodQuery.data.expired && (
                  <Alert variant="outlined" severity="error">
                    Your credit card has expired
                  </Alert>
                )}
                <EditPaymentMethodDialogue
                  open={editingCC}
                  handleOnClose={handleOnClose}
                  stripeUrl={stripeUrl}
                  paymentMethodMutation={paymentMethodMutation}
                />
              </Fragment>
            )}
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <Button
          size="small"
          color="inherit"
          disabled={!props.canCancel}
          onClick={props.onCancel}
        >
          Cancel
        </Button>
        <LoadingButton
          size="small"
          loading={props.isLoading}
          variant="contained"
          disabled={!props.canSubmit}
          onClick={props.onSubmit}
        >
          Purchase
        </LoadingButton>
      </CardActions>
    </Card>
  );
}
